/**
 * Botón del avatar y su menú: quién eres, «Ir a» (solo en celular), apariencia, «Mi cuenta y tokens» y
 * «Cerrar sesión».
 *
 * Es un diálogo no modal: Esc o clic fuera lo cierran y el foco vuelve al botón. Los cambios de
 * apariencia se aplican al instante (no hay «Guardar»).
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router';
import { ETIQUETA_ROL } from '@cdtec-backlog/contrato';
import { useCerrarSesion, useYo } from '../features/auth/sesion';
import { ControlesApariencia } from '../features/tema/ControlesApariencia';
import { useTema } from '../features/tema/contexto';
import { iniciales } from './Avatar';
import { Icono } from './Icono';
import { usePaginasPrincipales } from './paginasPrincipales';
import './MenuPerfil.css';

export function MenuPerfil() {
  const yo = useYo();
  const cerrarSesion = useCerrarSesion();
  const { restablecer } = useTema();
  const [abierto, setAbierto] = useState(false);
  const boton = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const idMenu = useId();
  const paginas = usePaginasPrincipales();

  useEffect(() => {
    if (!abierto) return;
    menu.current?.focus();
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Captura + preventDefault: el panel de tarea abierto detrás ignora los Esc ya atendidos.
        e.preventDefault();
        setAbierto(false);
        boton.current?.focus();
      }
    };
    const alPulsar = (e: MouseEvent) => {
      const destino = e.target as Node;
      if (!menu.current?.contains(destino) && !boton.current?.contains(destino)) setAbierto(false);
    };
    document.addEventListener('keydown', alTeclear, true);
    document.addEventListener('mousedown', alPulsar);
    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      document.removeEventListener('mousedown', alPulsar);
    };
  }, [abierto]);

  const listo = () => {
    setAbierto(false);
    boton.current?.focus();
  };

  return (
    <div className="perfil">
      <button
        ref={boton}
        type="button"
        className="perfil__boton"
        aria-label="Mi perfil y apariencia"
        title="Mi perfil y apariencia"
        aria-expanded={abierto}
        aria-controls={abierto ? idMenu : undefined}
        aria-haspopup="dialog"
        onClick={() => setAbierto((v) => !v)}
      >
        <span className="avatar" aria-hidden="true">
          {iniciales(yo.nombre)}
        </span>
        <span className="perfil__chevron" aria-hidden="true" />
      </button>

      {abierto && (
        <div
          ref={menu}
          id={idMenu}
          role="dialog"
          aria-label="Mi perfil y apariencia"
          tabIndex={-1}
          className="perfil__menu"
        >
          {/*
            Siempre en el DOM; el CSS la muestra solo con la barra angosta, cuando las pestañas del
            encabezado se ocultan (misma consulta de contenedor, ver MenuPerfil.css).
          */}
          <nav className="perfil__ir" aria-labelledby={`${idMenu}-ir`}>
            <h2 id={`${idMenu}-ir`} className="perfil__titulo">
              Ir a
            </h2>
            {paginas.map((pagina) => (
              <NavLink key={pagina.a} to={pagina.a} end={pagina.fin} onClick={listo}>
                <Icono nombre={pagina.icono} />
                {pagina.texto}
              </NavLink>
            ))}
          </nav>

          <div className="perfil__quien">
            <span className="avatar avatar--grande" aria-hidden="true">
              {iniciales(yo.nombre)}
            </span>
            <div>
              <strong>{yo.nombre}</strong>
              <small>
                {yo.email ? `${yo.email} · ` : ''}
                {ETIQUETA_ROL[yo.rol]}
              </small>
            </div>
          </div>

          <p className="perfil__nota">La apariencia se guarda solo en este navegador.</p>
          <ControlesApariencia />

          <div className="perfil__pie">
            <button type="button" className="boton" onClick={restablecer}>
              Restablecer
            </button>
            <button type="button" className="boton boton--primario" onClick={listo}>
              Listo
            </button>
          </div>

          <div className="perfil__enlaces">
            <Link to="/cuenta" onClick={listo}>
              <Icono nombre="cuenta" />
              Mi cuenta y tokens
            </Link>
            <button type="button" onClick={() => void cerrarSesion()}>
              <Icono nombre="salir" />
              Cerrar sesión
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
